import {
  CollectionContext,
  OrganizationDataRecipientDto,
  OrganizationDataSourceDto,
} from "../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { isGoogleAnalytics, isShopifyAudiences, isStorageLocation } from "../../../util/vendors";
import {
  SOURCE_KEY_CONSUMER_DIRECTLY,
  VENDOR_CATEGORY,
  VENDOR_CATEGORY_AD_NETWORK,
  VENDOR_CLASSIFICATION_SLUG_SERVICE_PROVIDER,
  VENDOR_KEY_SHOPIFY_AUDIENCES,
  VENDOR_KEY_TRUEVAULT,
} from "../../../types/Vendor";
import { Api } from "../../../../common/service/Api";
import { SurveyAnswers } from "../../../../common/hooks/api/useSurvey";

/** Hidden from consumer vendor inventory; still exists for HR-only data map usage. */
export function isEmployeeOnlyCollectionContext(collectionContext: CollectionContext[]): boolean {
  return (
    collectionContext.length > 0 &&
    collectionContext.includes("EMPLOYEE") &&
    !collectionContext.includes("CONSUMER")
  );
}

/** Show empty shells and consumer (or consumer+employee) vendors; hide employee-only. */
export function shouldShowInVendorInventory(collectionContext: CollectionContext[]): boolean {
  return !isEmployeeOnlyCollectionContext(collectionContext);
}

export function hasConsumerCollectionContext(collectionContext: CollectionContext[]): boolean {
  return collectionContext.includes("CONSUMER");
}

export function shouldShowSharing(
  vendor: OrganizationDataRecipientDto,
  pageContext: CollectionContext,
): boolean {
  if (isEmployeeOnlyCollectionContext(vendor.collectionContext ?? [])) {
    return false;
  }

  if (!vendor.collectionContext?.includes(pageContext)) {
    return false;
  }

  // Service providers have sharing question disabled (POL-7455)
  if (vendor.classification?.slug === VENDOR_CLASSIFICATION_SLUG_SERVICE_PROVIDER) {
    return false;
  }

  if (isStorageLocation(vendor)) {
    return false;
  }

  // If vendor is non-custom, then don't show for contractors
  const isContractor = vendor.classification.slug === "contractor";
  if (!vendor.isCustom && isContractor) {
    return false;
  }

  // List of allowed categories from POL-6117
  const sharingVendorCategories = [
    VENDOR_CATEGORY.AD_NETWORK,
    VENDOR_CATEGORY.SALES_MARKETING_TOOL,
    VENDOR_CATEGORY.DATA_ANALYTICS_PROVIDER,
    VENDOR_CATEGORY.DATA_BROKER,
    "Data Cooperative", // Not in constants but exists in system
  ];
  return sharingVendorCategories.includes(vendor.category);
}

export function doesVendorHaveSharingComplete(vendor: OrganizationDataRecipientDto): boolean {
  if (vendor.ccpaIsSharing == null || vendor.ccpaIsSharing == undefined) {
    return false;
  }

  if (vendor.ccpaIsSharing == true) {
    if (vendor.usesCustomAudience == null || vendor.usesCustomAudience == undefined) {
      return false;
    }
  }

  return true;
}

/**
 * Determines whether the "selling" question should be shown for a vendor.
 *
 * This is an isolated version of the `showSelling` logic from `calculateDisclosureMeta`
 * in DataRecipientDisclosureSettings.
 *
 */
export function shouldShowSelling(
  vendor: OrganizationDataRecipientDto,
  pageContext: CollectionContext,
): boolean {
  if (isEmployeeOnlyCollectionContext(vendor.collectionContext ?? [])) {
    return false;
  }

  if (!vendor.collectionContext?.includes(pageContext)) {
    return false;
  }

  // Service providers have selling question disabled (POL-7455)
  if (vendor.classification?.slug === VENDOR_CLASSIFICATION_SLUG_SERVICE_PROVIDER) {
    return false;
  }

  const classifiedAsContractor = vendor.classification?.slug === "contractor";
  const classifiedAsThirdParty = vendor.classification?.slug === "third-party";

  const isAdNetwork = vendor.category === VENDOR_CATEGORY_AD_NETWORK;

  return (
    !isStorageLocation(vendor) &&
    (vendor.isCustom
      ? !classifiedAsContractor && !isAdNetwork
      : isShopifyAudiences(vendor) ||
        (!classifiedAsContractor &&
          !isAdNetwork &&
          (classifiedAsThirdParty || vendor?.serviceProviderRecommendation != "SERVICE_PROVIDER")))
  );
}

export function shouldShowDataSellingSection(
  vendor: OrganizationDataRecipientDto,
  pageContext: CollectionContext,
): boolean {
  // HR-only vendors are excluded even when a vendor has special selling behavior.
  if (isEmployeeOnlyCollectionContext(vendor.collectionContext ?? [])) {
    return false;
  }

  // Google Analytics is special: its Data Selling section hosts the GA data-sharing question, and answering
  // "sharing off" automatically classifies GA as a service provider. The GA check must come before the
  // service-provider guard below — otherwise answering the question hides the section, and the user can never
  // see or change their answer again.
  if (isGoogleAnalytics(vendor)) {
    return true;
  }

  if (!vendor.collectionContext?.includes(pageContext)) {
    return false;
  }

  // Service providers have selling question disabled (POL-7455)
  if (vendor.classification?.slug === VENDOR_CLASSIFICATION_SLUG_SERVICE_PROVIDER) {
    return false;
  }

  return shouldShowSelling(vendor, pageContext);
}

export function doesVendorHaveSellingComplete(
  vendor: OrganizationDataRecipientDto,
  sellingAndSharingAnswers: SurveyAnswers,
): boolean {
  if (isGoogleAnalytics(vendor) && !hasGoogleAnalyticsDataSharingAnswer(sellingAndSharingAnswers)) {
    return false;
  }

  if (vendor.isAlwaysDataSellingVendor) {
    return true;
  }

  // Check for locked value
  const lockedSellingValues = isDataSellingLockedForVendor(vendor, sellingAndSharingAnswers);
  if (lockedSellingValues.dataSelling !== null) {
    return true;
  }

  return vendor.ccpaIsSelling !== undefined && vendor.ccpaIsSelling !== null;
}

export function hasGoogleAnalyticsDataSharingAnswer(
  sellingAndSharingAnswers: SurveyAnswers,
): boolean {
  const answer = sellingAndSharingAnswers["google-analytics-data-sharing"];
  return answer === "true" || answer === "false";
}

// Returns the locked data selling value, or null if the data selling is not locked for this vendor
export function isDataSellingLockedForVendor(
  vendor: OrganizationDataRecipientDto,
  sellingAndSharingAnswers: SurveyAnswers,
): {
  dataSelling: boolean | null;
  dataSellingLockedTooltipMessage?: string;
} {
  // Shopify Audiences (SNI) always has selling enabled
  if (vendor.vendorKey === VENDOR_KEY_SHOPIFY_AUDIENCES) {
    return {
      dataSelling: true,
      // dataSellingIsLocked: true,
      dataSellingLockedTooltipMessage:
        "Shopify Network Intelligence shares your customers' data with third parties for marketing purposes, which is considered “selling” in some regions.",
    };
  }

  // Google Analytics selling is based on the survey question
  if (isGoogleAnalytics(vendor)) {
    const isGoogleAnalyticsDataSharing =
      sellingAndSharingAnswers["google-analytics-data-sharing"] == "true";
    const gaIsSellingState = !isGoogleAnalyticsDataSharing;
    return {
      dataSelling: gaIsSellingState,
      dataSellingLockedTooltipMessage: gaIsSellingState
        ? "You have indicated you are using features which require this data recipient to be considered “selling” data."
        : "You have indicated you are using features which require this data recipient not to be considered “selling” data.",
    };
  }

  if (vendor.isAlwaysDataSellingVendor) {
    return {
      dataSelling: true,
    };
  }

  return {
    dataSelling: null,
  };
}

// Returns the locked upload value, or null if upload is not locked for this vendor
export function isUploadLockedForVendor(vendor: OrganizationDataRecipientDto): {
  upload: boolean | null;
  uploadLockedTooltipMessage?: string;
} {
  if (vendor.isAlwaysUploadVendor) {
    return {
      upload: true,
      uploadLockedTooltipMessage:
        "Our research indicates that this vendor is always uploading data.",
    };
  }

  return {
    upload: null,
  };
}

/* ------------------------------------------------------------------------------------------------
 * Vendor inventory shaping and removal.
 *
 * These live here rather than in VendorInventoryPage so the Data Flows pages and the standalone
 * settings modal can reuse them without importing the page module, which closed an import cycle
 * (VendorInventoryPage -> VendorDetailsModal -> DataFlowsDisclosuresPage -> VendorInventoryPage).
 * ---------------------------------------------------------------------------------------------- */

export type VendorInventoryItem = {
  vendorId: string;
  name: string;
  category?: string;
  defaultCategory?: string | null;
  url?: string;
  logoUrl?: string;
  vendorKey?: string;
  isCustom: boolean;
  isSource: boolean;
  isRecipient: boolean;
  recipientData?: OrganizationDataRecipientDto;
  collectionContext: CollectionContext[];
  /** Internal data storage locations (`storage_location`); rendered in a dedicated table section. */
  isInternalDataStorageLocation?: boolean;
};

export const hasInventoryCollectionContext = (
  collectionContext: CollectionContext[],
  context: CollectionContext,
): boolean => collectionContext?.includes(context);

export const shouldShowInInventoryContext = (
  collectionContext: CollectionContext[],
  context: CollectionContext,
  includeEmptyContext: boolean,
): boolean => {
  if (context === "CONSUMER") {
    return shouldShowInVendorInventory(collectionContext);
  }

  if (includeEmptyContext && collectionContext.length === 0) {
    return true;
  }

  return hasInventoryCollectionContext(collectionContext, context);
};

/** The Vendor List's data-source visibility rule, reusable outside the page. */
export function filterInventoryDataSources(
  dataSources: OrganizationDataSourceDto[],
  collectionContext: CollectionContext,
  includeEmptyContext: boolean,
): OrganizationDataSourceDto[] {
  return dataSources.filter((source) => {
    if (
      !shouldShowInInventoryContext(
        source.collectionContext,
        collectionContext,
        includeEmptyContext,
      )
    ) {
      return false;
    }
    // Don't show the "Consumer" source which is auto-created and isn't a vendor
    return source.vendorKey !== SOURCE_KEY_CONSUMER_DIRECTLY;
  });
}

/** Builds the inventory rows from recipients + (already filtered) data sources. */
export function buildVendorInventoryItems({
  recipients,
  dataSources,
  collectionContext,
  includeEmptyContext,
  alwaysIncludeVendorIds,
}: {
  recipients: OrganizationDataRecipientDto[];
  dataSources: OrganizationDataSourceDto[];
  collectionContext: CollectionContext;
  includeEmptyContext: boolean;
  /** Vendors kept regardless of context — e.g. a settings modal opened for a specific vendor
   *  must keep resolving it after an edit subtracts its collection context (POL-7818). */
  alwaysIncludeVendorIds?: Set<string>;
}): VendorInventoryItem[] {
  const isInventoryMember = (v: OrganizationDataRecipientDto) =>
    shouldShowInInventoryContext(v.collectionContext, collectionContext, includeEmptyContext) ||
    (alwaysIncludeVendorIds?.has(v.vendorId) ?? false);
  const nonStorageRecipients = recipients.filter(
    (v) => !isStorageLocation(v) && isInventoryMember(v),
  );
  const storageRecipients = recipients.filter((v) => isStorageLocation(v) && isInventoryMember(v));

  const isInventoryContextSource = (vendorId: string) =>
    dataSources.some(
      (ds) =>
        ds.vendorId === vendorId &&
        hasInventoryCollectionContext(ds.collectionContext, collectionContext),
    );

  const items: VendorInventoryItem[] = [];

  for (const v of nonStorageRecipients) {
    items.push({
      vendorId: v.vendorId,
      name: v.name,
      category: v.category,
      defaultCategory: v.defaultCategory,
      url: v.url,
      logoUrl: v.logoUrl,
      vendorKey: v.vendorKey,
      isCustom: v.isCustom,
      isSource: isInventoryContextSource(v.vendorId),
      isRecipient: hasInventoryCollectionContext(v.collectionContext, collectionContext),
      recipientData: v,
      collectionContext: v.collectionContext,
      isInternalDataStorageLocation: false,
    });
  }

  // Source-only vendors are deliberately not listed: the Vendor List is the org's data
  // recipients, and sources are managed on the Sources page (the header says so).

  for (const v of storageRecipients) {
    items.push({
      vendorId: v.vendorId,
      name: v.name,
      category: v.category,
      defaultCategory: v.defaultCategory,
      url: v.url,
      logoUrl: v.logoUrl,
      vendorKey: v.vendorKey,
      isCustom: v.isCustom,
      isSource: isInventoryContextSource(v.vendorId),
      isRecipient: hasInventoryCollectionContext(v.collectionContext, collectionContext),
      recipientData: v,
      collectionContext: v.collectionContext,
      isInternalDataStorageLocation: true,
    });
  }

  return items;
}

/** Completely removes a vendor from the org as both a data source and data recipient. */
export async function removeVendorCompletely(
  organizationId: string,
  vendorId: string,
): Promise<void> {
  await Api.organizationVendor.removeVendor(
    organizationId,
    vendorId,
    /* collectionContext = */ undefined,
    /* deleteOnEmpty = */ true,
    /* removeFromDataMap = */ true,
  );
}

/** Shopify Audiences / SNI removal has survey side-effects that require a full page reload. */
export function vendorRemovalRequiresPageReload(vendorKey?: string | null): boolean {
  return vendorKey === VENDOR_KEY_SHOPIFY_AUDIENCES;
}

export function isVendorRemovable(vendorKey?: string | null): boolean {
  return vendorKey !== VENDOR_KEY_TRUEVAULT;
}
